import React from "react";
import PropTypes from "prop-types";
import moment from "moment";
import { connect } from 'react-redux';
import { LuFileSearch2 } from "react-icons/lu";
import { Button, Checkbox, Icon, IconButton } from "@cbrebuild/blocks";
import Avatar from "../../nucleus/avatar/avatar";
import ConfirmationModal from "../../nucleus/confirmation-modal/confirmation-modal";
import dealsService from "../../services/deals-service";
import { getAttachmentsExtract, postAttachmentsToExtract, startDealExtractionPolling, subscribeToDealExtractionStatus } from "../../services/attachments-service";
import userEventService from "../../services/user-event-service";
import mockData from "./test/test.json";
import "./lease-abstraction.scss";
import LoadingIcon from '../../assets/images/svg/loading-icon.svg';
import ExtractionTriggerIcon from '../../assets/images/svg/extraction-trigger-icon.svg';
import { abstractionLeaseFile } from '../../redux/actions/deal-files-actions';
import { toast } from '../../redux/actions/toasts-actions';
import FileModal from './file-modal';

const isOfacFileName = filename => Boolean(filename && filename.toLowerCase().includes('ofac'));
const isOfacFile = file => [file && file.title, file && file.document_name].some(isOfacFileName);
const getFileExtension = (filename) => {
  const lastDot = filename.lastIndexOf('.');
  return lastDot > 0 ? filename.substring(lastDot) : '';
};

const getExtensionFromFileType = (fileType) => {
  const normalizedFileType = String(fileType || '').trim().toLowerCase();
  if (!normalizedFileType) return '';

  const mimeToExtension = {
    'application/pdf': 'pdf',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'xlsx',
    'application/vnd.ms-excel': 'xls',
    'application/msword': 'doc',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'docx',
  };

  if (mimeToExtension[normalizedFileType]) {
    return `.${mimeToExtension[normalizedFileType]}`;
  }

  // Preserve known extension-like tokens only.
  return /^[a-z0-9]+$/.test(normalizedFileType) ? `.${normalizedFileType}` : '';
};

// Curated list of common file extensions we allow to be stripped from a
// user-typed title. Using a fixed list (instead of a generic "any short
// alnum token after a dot" regex) prevents mangling titles that legitimately
// contain a dot followed by a short word/number, e.g. "Client.24" or
// "v1.2 Report".
const COMMON_FILE_EXTENSIONS = [
  'pdf', 'xlsx', 'xls', 'doc', 'docx', 'ppt', 'pptx', 'csv', 'txt', 'rtf',
  'png', 'jpg', 'jpeg', 'gif', 'bmp', 'tif', 'tiff', 'heic', 'webp', 'svg',
  'zip', 'msg', 'eml', 'json', 'xml', 'html', 'htm',
];

const removeKnownTrailingExtension = (title, extensions = []) => {
  const normalizedTitle = String(title || '').trim();
  if (!normalizedTitle) return '';

  const lowerTitle = normalizedTitle.toLowerCase();
  const candidateExtensions = [
    ...extensions.map(ext => String(ext || '').toLowerCase()).filter(Boolean),
    ...COMMON_FILE_EXTENSIONS.map(ext => `.${ext}`),
  ];

  const extensionToRemove = candidateExtensions.find(ext => lowerTitle.endsWith(ext));
  if (!extensionToRemove) return normalizedTitle;

  return normalizedTitle.slice(0, normalizedTitle.length - extensionToRemove.length).trim();
};

// Resolves a friendly, short extension label (e.g. "pdf", "xlsx") to use for
// both the display text and the CSS class suffix, instead of showing/using
// the raw file_type value which can be a full MIME type. Derived from
// file_type (not the file name) so it reflects the file's actual reported type.
const resolveFileExtensionLabel = (file) => {
  const extensionFromType = getExtensionFromFileType(file.file_type).replace('.', '');
  if (extensionFromType) return extensionFromType.toLowerCase();

  const rawType = String(file.file_type || '').trim().toLowerCase();
  if (!rawType) return 'file';

  // Unrecognized/unmapped MIME types: avoid ever showing the full raw MIME
  // string to the user. Try to derive a short, readable token from the
  // subtype, otherwise fall back to a generic label.
  const lastSegment = rawType.includes('/') ? rawType.split('/').pop() : rawType;
  const shortToken = (lastSegment || '').split(/[.+]/).pop() || '';
  return /^[a-z0-9-]{1,12}$/.test(shortToken) ? shortToken : 'file';
};

// The CSS class applies text-transform: uppercase for display, so the label
// itself stays lowercase here (keeps class-name derivation and text content consistent).
const getFileTypeDisplayLabel = file => resolveFileExtensionLabel(file);

const getFileTypeClassSuffix = (file) => {
  const label = resolveFileExtensionLabel(file);
  // Keep class names CSS-safe and predictable.
  return label.replace(/[^a-z0-9_-]/g, '-') || 'unknown';
};

const stripFileExtension = (filename) => {
  const lastDot = filename.lastIndexOf('.');
  return lastDot > 0 ? filename.substring(0, lastDot) : filename;
};

const SparkleGlyph = () => (
  <svg className="files-tab-sparkle-svg" width="24" height="24" viewBox="0 0 24 24" aria-hidden="true">
    <path d="M9 2 L10.6 6.8 L15.5 8.5 L10.6 10.2 L9 15 L7.4 10.2 L2.5 8.5 L7.4 6.8 Z" fill="currentColor" />
    <path d="M18 3 L18.9 5.6 L21.5 6.5 L18.9 7.4 L18 10 L17.1 7.4 L14.5 6.5 L17.1 5.6 Z" fill="currentColor" />
    <path d="M17.5 13.5 L18.2 15.6 L20.3 16.3 L18.2 17 L17.5 19.1 L16.8 17 L14.7 16.3 L16.8 15.6 Z" fill="currentColor" />
  </svg>
);

export class FileListItem extends React.Component {
  unsubscribeExtractionStatus = null;

  state = {
    tempPrivacy: this.props.file.privacy.toLowerCase() !== "team",
    tempTitle: stripFileExtension(this.props.file.document_name || this.props.file.title || ''),
    showEditMode: false,
    showModal: false,
    showDataModal: false,
    processingCompleted: false,
    dropdownOpen: Array(mockData.results.extracted_response.length).fill(false),
    results: [],
    isResultEmpty: false,
    errorMessage: "",
    successMessage: "",
    extractionIconState: 'trigger', // 'trigger' | 'loading' | 'search'
    postUploadTriggered: false, // Track if this file was triggered post-upload
    showToastOnCompletion: false, // Only show toast for newly initiated extractions
    extractionStatusChecked: false, // Don't show any status text until initial API check resolves
    enhancedRetrying: false, // Tracks retry in progress for enhanced flow
    extractionStatus: null,
  };

  componentDidMount() {
    this.subscribeToExtractionStatus();
  }

  componentWillUnmount() {
    if (this.unsubscribeExtractionStatus) {
      this.unsubscribeExtractionStatus();
    }
  }

  componentDidUpdate(prevProps) {
    const prevName = prevProps.file.document_name || prevProps.file.title || '';
    const nextName = this.props.file.document_name || this.props.file.title || '';
    const prevPrivacyIsPrivate = prevProps.file.privacy.toLowerCase() !== 'team';
    const nextPrivacyIsPrivate = this.props.file.privacy.toLowerCase() !== 'team';

    const fileIdentityChanged = prevProps.file.id !== this.props.file.id;
    const nameChanged = prevName !== nextName;
    const privacyChanged = prevPrivacyIsPrivate !== nextPrivacyIsPrivate;

    if (!fileIdentityChanged && !nameChanged && !privacyChanged) {
      return;
    }

    // Only clobber local state if the user hasn't made unsaved edits away
    // from what was previously loaded from props. This lets us pick up
    // concurrent server-side updates (e.g. another session saving the same
    // file) even while edit mode is open, without discarding in-progress
    // typing. A file identity change always forces a full resync since any
    // local state necessarily belonged to a different file.
    const derivedPrevTitle = stripFileExtension(prevName);
    const hasUnsavedTitleEdit = !fileIdentityChanged && this.state.tempTitle !== derivedPrevTitle;
    const hasUnsavedPrivacyEdit = !fileIdentityChanged && this.state.tempPrivacy !== prevPrivacyIsPrivate;

    const nextState = {};
    if (fileIdentityChanged || !hasUnsavedTitleEdit) {
      nextState.tempTitle = stripFileExtension(nextName);
    }
    if (fileIdentityChanged || !hasUnsavedPrivacyEdit) {
      nextState.tempPrivacy = nextPrivacyIsPrivate;
    }

    if (Object.keys(nextState).length > 0) {
      this.setState(nextState);
    }
  }

  subscribeToExtractionStatus = () => {
    const { file, dealType, isEnhancedFlow } = this.props;
    const normalizedDealType = String(dealType || '').toLowerCase();
    // Subscribe for files with known extraction history. In enhanced flow,
    // also subscribe to fetch 'Stopped' so sparkle can render in Files tab.
    const hasExtractionHistory = file.extraction_opted_in || file.is_lease_document;
    const shouldSubscribe = normalizedDealType === 'lease' &&
      file.id && file.deal &&
      !isOfacFile(file) &&
      (hasExtractionHistory || isEnhancedFlow);
    if (!shouldSubscribe) return;

    if (this.unsubscribeExtractionStatus) {
      this.unsubscribeExtractionStatus();
    }

    this.unsubscribeExtractionStatus = subscribeToDealExtractionStatus({
      dealId: file.deal,
      fileId: file.id,
      onUpdate: this.handleExtractionStatusUpdate,
      onError: () => {
        this.setState({ processingCompleted: true, extractionStatusChecked: true });
      },
    });
  };

  handleExtractionStatusUpdate = (result) => {
    const results = result ? [result] : [];
    this.applyExtractionResults(results);
  };

  applyExtractionResults = (results = []) => {
    const { file, showToast, isEnhancedFlow } = this.props;
    const extractionResult = results[0] || null;
    const extractionStatus = extractionResult ? extractionResult.status : null;
    const isQueuedOrProcessing = extractionStatus === 'Queued' || extractionStatus === 'Processing';
    const hasExtractionError = extractionResult && extractionResult.status_text && extractionResult.status_text.includes(
      'Exception occured while extracting data by file key'
    );
    const isUnsupportedFileType = extractionResult && extractionResult.status_text && extractionResult.status_text.includes(
      'Document extraction is not processed for this file type'
    );
    const isCompleted = Boolean(extractionResult) && (
      extractionStatus === 'Success' ||
      extractionStatus === 'Failure' ||
      extractionStatus === 'Stopped' ||
      isUnsupportedFileType
    );

    if (!extractionResult) {
      const shouldKeepWaiting = this.state.postUploadTriggered || this.state.showToastOnCompletion || this.state.enhancedRetrying;
      this.setState({
        processingCompleted: shouldKeepWaiting ? false : true,
        extractionStatusChecked: true,
        isResultEmpty: !shouldKeepWaiting,
        extractionStatus: null,
        ...(this.state.postUploadTriggered ? { extractionIconState: 'loading' } : {}),
      });
      return;
    }

    if (hasExtractionError) {
      this.setState({
        processingCompleted: true,
        extractionStatusChecked: true,
        isResultEmpty: true,
        errorMessage: this.getErrorMessage(results),
        successMessage: this.getSuccessMessage(results),
        extractionStatus,
        ...(this.state.postUploadTriggered ? { extractionIconState: 'error' } : {}),
      });

      if (this.state.showToastOnCompletion) {
        showToast(`Failed to extract data for ${file.title}`);
        this.setState({ showToastOnCompletion: false });
      }
      return;
    }

    this.setState({
      processingCompleted: isCompleted,
      extractionStatusChecked: true,
      results,
      isResultEmpty: false,
      errorMessage: this.getErrorMessage(results),
      successMessage: this.getSuccessMessage(results),
      extractionStatus,
    });

    if (!isEnhancedFlow && this.state.postUploadTriggered) {
      if (isQueuedOrProcessing) {
        this.setState({ extractionIconState: 'loading' });
      } else if (extractionStatus === 'Success' && !hasExtractionError && !isUnsupportedFileType) {
        this.setState({ extractionIconState: 'search' });
      } else if (extractionStatus === 'Stopped') {
        this.setState({ extractionIconState: 'trigger' });
      } else if (isCompleted) {
        this.setState({ extractionIconState: 'error' });
      }
    }

    if (isCompleted && this.state.showToastOnCompletion) {
      if (extractionStatus === 'Success' && !isUnsupportedFileType) {
        showToast(`Successfully extracted data for ${file.title}`);
      } else if (extractionStatus === 'Failure' || isUnsupportedFileType) {
        showToast(`Failed to extract data for ${file.title}`);
      }
      this.setState({ showToastOnCompletion: false });
    }
  };

  handleEnhancedRetry = () => {
    const { file, updateFile } = this.props;
    this.setState({
      enhancedRetrying: true,
      processingCompleted: false,
      extractionStatusChecked: true,
      extractionStatus: 'Processing',
      errorMessage: '',
      showToastOnCompletion: true,
    });

    const optInPromise = file.extraction_opted_in
      ? Promise.resolve()
      : Promise.resolve(updateFile(file.id, {
        ...file,
        extraction_opted_in: true,
      }));

    optInPromise
      .then(() => postAttachmentsToExtract({
        deal: file.deal,
        file: file.id,
        attachment_file_key: null,
        is_abstract: true,
      }))
      .then(() => startDealExtractionPolling(file.deal, file.id, { initialDelayMs: 12000 }))
      .then(() => {
        this.setState({ enhancedRetrying: false });
      })
      .catch(() => this.setState({
        enhancedRetrying: false,
        processingCompleted: true,
        extractionStatus: 'Failure',
        errorMessage: 'Abstraction failed',
      }));
  };

  openDataModal = () => {
    const { file } = this.props;
    // Only fetch extraction data if user opted in during upload
    if (file.extraction_opted_in) {
      getAttachmentsExtract({ deal: file.deal, file: file.id }).then((res) => {
        this.setState({
          processingCompleted:
            res.result[0].status === "Success" ||
            res.result[0].status === "Failure",
        });
        this.setState({ results: res.result });
      });
    }
    this.setState({ showDataModal: true });
  };

  handleExtract = () => {
    if (!this.state.processingCompleted) {
      const { file } = this.props;
      // Only fetch extraction data if user opted in during upload
      if (file.extraction_opted_in) {
        getAttachmentsExtract({ deal: file.deal, file: file.id }).then((res) => {
          this.setState({
            processingCompleted:
              res.result[0].status === "Success" ||
              res.result[0].status === "Failure",
          });
          this.setState({ results: res.result });
        });
      }
    }
  };

  getSuccessMessage = (results) => {
    if (results[0]?.status === 'Success' && results[0]?.status_text === 'Data extracted successfully') {
      return "Lease import to voucher is complete.";
    }
    return "";
  };

  getErrorMessage = (results) => {
    if (results.length === 0) {
      // no message, no icon
      return "";
    } else if (
      // no icon, display error message for unsupported file type
      results[0]?.status_text && results[0].status_text.includes(
        "Document extraction is not processed for this file type"
      )
    ) {
      return "Document abstraction is enabled only for PDF files.";
    } else if (
      // display loading icon, no error message for documents still being processed
      results[0]?.status === "Queued" ||
      results[0]?.status === "Processing"
    ) {
      return "";
    } else if (
      // no icon, display error message for failed documents
      results[0]?.status === "Failure"
    ) {
      return "Abstraction failed";
    }

    // Default case: otherwise it would return undefined.
    return "";
  };

  removeFileSelected = () => {
    // Get all file list items
    const fileItems = document.querySelectorAll(".file-list-item");
    // Remove the 'lease-abstraction-file-selected' class from all file list items
    fileItems.forEach((fileItem) => {
      fileItem.classList.remove("lease-abstraction-file-selected");
      // Get the file search icon of the file list item
      const fileSearchIcon = fileItem.querySelector(".fileSearchIcon");
      // Remove the 'disabled' class from the file search icon
      if (fileSearchIcon) {
        fileSearchIcon.classList.remove("disabled");
      }
    });
  };

  hideDataModal = () => {
    const { file, leaseFileUpdate } = this.props;
    this.setState({ showDataModal: false });
    localStorage.setItem(`leaseDeal_${file.deal}`, JSON.stringify(file));
    leaseFileUpdate(file);
    this.removeFileSelected();
  };

  closeDataModal = () => {
    const { file, leaseFileUpdate } = this.props;
    this.setState({ showDataModal: false });
    localStorage.removeItem(`leaseDeal_${file.deal}`);
    leaseFileUpdate({});
    this.removeFileSelected();
  };

  handleClick = () => {
    // Get all file list items
    const fileItems = document.querySelectorAll(".file-list-item");
    // Remove the 'selected' and 'disabled' classes from all file list items
    fileItems.forEach((fileItem) => {
      const leaseAbstractionFileSelected = fileItem.querySelector(
        ".lease-abstraction-file-selected"
      );
      if (leaseAbstractionFileSelected) {
        leaseAbstractionFileSelected.classList.remove(
          "lease-abstraction-file-selected"
        );
      }
      const fileSearchIcon = fileItem.querySelector(".fileSearchIcon");
      if (fileSearchIcon) {
        fileSearchIcon.classList.remove("disabled");
      }
    });
    // Add the 'selected' class to the clicked file list item
    this.liElement.classList.add("lease-abstraction-file-selected");
    // Add the 'disabled' class to the file search icons of all other file list items
    fileItems.forEach((fileItem) => {
      if (fileItem !== this.liElement) {
        const fileSearchIcon = fileItem.querySelector(".fileSearchIcon");
        if (fileSearchIcon) {
          fileSearchIcon.classList.add("disabled");
        }
      }
    });
  };

  toggleDropdown(i) {
    this.setState((prevState) => {
      const dropdownOpen = prevState.dropdownOpen.slice();
      dropdownOpen[i] = !dropdownOpen[i];
      return { dropdownOpen };
    });
  }

  closeConfirmationModal = () => {
    this.setState({ showModal: false });
  };

  closeEditMode = () => {
    this.setState({ showEditMode: false });
  };

  handleDeleteFile = (id) => {
    const { deleteFile, file } = this.props;
    deleteFile(id);

    dealsService.updateDeal(file.deal, {
      modified: new Date(),
    });

    this.closeConfirmationModal();
    this.trackDeleteFile();
  };

  handleFileDownload = (id) => {
    const { fetchFilesDownloadLink } = this.props;
    fetchFilesDownloadLink(id, true);
    this.trackDownloadFile();
  };

  handleFileNameChange = (value) => {
    this.setState({ tempTitle: value });
  };

  handlePrivacyChange = (value) => {
    this.setState({ tempPrivacy: value });
  };

  handleSave = () => {
    const { file, updateFile } = this.props;
    const { tempPrivacy, tempTitle } = this.state;
    const originalName = file.document_name || file.title || '';
    const fallbackTitle = stripFileExtension(originalName);
    const extension = getFileExtension(originalName) || getExtensionFromFileType(file.file_type);
    const trimmedTempTitle = (tempTitle || '').trim();
    const titleWithoutKnownExtension = removeKnownTrailingExtension(trimmedTempTitle, [extension]);
    // Always keep a non-empty base name so we never save a document_name
    // that is just an extension (e.g. ".pdf") when the file has no usable
    // existing name to fall back to either.
    const sanitizedTitle = titleWithoutKnownExtension || fallbackTitle || 'Untitled';
    const documentName = `${sanitizedTitle}${extension}`;
    const currentPrivacyIsPrivate = String(file.privacy || '').toLowerCase() !== 'team';
    const params = {
      ...file,
      title: sanitizedTitle,
      document_name: documentName,
      privacy: tempPrivacy ? "private" : "team",
    };
    if (tempPrivacy !== currentPrivacyIsPrivate) {
      this.trackPrivacyChange(tempPrivacy ? "private" : "team");
    }
    if (documentName !== file.document_name) {
      this.trackNameChange(documentName);
    }

    updateFile(file.id, params);

    dealsService.updateDeal(file.deal, {
      modified: new Date(),
    });
    this.closeEditMode();
  };

  showConfirmationModal = () => {
    this.setState({ showModal: true });
  };

  showEditMode = () => {
    const { file } = this.props;
    this.setState({
      showEditMode: true,
      tempTitle: stripFileExtension(file.document_name || file.title || ''),
      tempPrivacy: file.privacy.toLowerCase() !== 'team',
    });
  };

  trackDeleteFile = () => {
    const {
      analyticProperties: { actionPrefix, categoryPrefix },
    } = this.props;
    userEventService.trackEvent(
      {
        eventAction: "file_deleted",
        eventCategory: "File Action",
      },
      {
        actionPrefix,
        categoryPrefix,
      },
    );
  };

  trackDownloadFile = () => {
    const {
      analyticProperties: { actionPrefix, categoryPrefix },
    } = this.props;
    userEventService.trackEvent(
      {
        eventAction: "file_downloaded",
        eventCategory: "File Action",
      },
      {
        actionPrefix,
        categoryPrefix,
      },
    );
  };

  trackLeaseAbstraction = () => {
    const {
      analyticProperties: { actionPrefix, categoryPrefix },
    } = this.props;
    userEventService.trackEvent(
      {
        eventAction: "detect_data_clicked",
        eventCategory: "File Action",
        eventValue: {
          deal_pk: this.props.file.deal,
          file_pk: this.props.file.id,
          file_name: this.props.file.title,
        },
      },
      {
        actionPrefix,
        categoryPrefix,
      },
    );
  };

  handleExtractionTrigger = () => {
    const { file, updateFile, showToast } = this.props;

    if (isOfacFile(file)) {
      showToast('OFAC documents are not eligible for document abstraction.');
      return;
    }

    // Show toast notification
    showToast(`Document abstraction is initiated for ${file.title}`);

    // Set loading state and mark as post-upload triggered
    this.setState({
      extractionIconState: 'loading',
      postUploadTriggered: true,
      showToastOnCompletion: true, // Show toast when this extraction completes
      errorMessage: false,
    });

    // Track extraction trigger event
    this.trackExtractionTrigger();

    // Update file to mark as opted-in for extraction, THEN trigger extraction.
    // Must await the PATCH before POSTing to attachmentextract — backend reads
    // att.extraction_opted_in from DB, so firing both in parallel causes a race
    // condition where the extract POST arrives before the PATCH commits.
    updateFile(file.id, {
      ...file,
      extraction_opted_in: true,
    }).then(() => {
      this.triggerPostUploadExtraction(file.id);
    });
  };

  trackExtractionTrigger = () => {
    const {
      analyticProperties: { actionPrefix, categoryPrefix },
    } = this.props;
    userEventService.trackEvent(
      {
        eventAction: "post_upload_extraction_triggered",
        eventCategory: "File Action",
        eventValue: {
          deal_pk: this.props.file.deal,
          file_pk: this.props.file.id,
          file_name: this.props.file.title,
        },
      },
      {
        actionPrefix,
        categoryPrefix,
      },
    );
  };

  triggerPostUploadExtraction = (fileId) => {
    const { file } = this.props;

    postAttachmentsToExtract({
      deal: file.deal,
      file: fileId,
      attachment_file_key: null,
    })
      .then(() => startDealExtractionPolling(file.deal, fileId))
      .catch(() => {
        this.setState({ extractionIconState: 'error' });
      });
  };

  renderExtractionIcon = () => {
    const { file, dealType, isEnhancedFlow } = this.props;
    const { extractionIconState, processingCompleted, isResultEmpty, errorMessage, postUploadTriggered } = this.state;

    // Don't show any extraction icons if not a lease deal or not a PDF file
    if (dealType !== 'lease' || file.file_type?.toLowerCase() !== 'pdf' || isOfacFile(file)) {
      return null;
    }

    // New enhanced flow — backend auto-extracts; no manual icons shown here.
    // Status text is rendered in the file-info section instead.
    if (isEnhancedFlow) {
      return null;
    }

    // For files that opted-in during upload (not post-upload triggered),
    // show the traditional extraction flow
    if (file.extraction_opted_in && extractionIconState === 'trigger' && !postUploadTriggered) {
      if (processingCompleted && !isResultEmpty && !errorMessage) {
        // Show search icon for successful extraction
        return (
          <div
            data-tooltip="Import Data to Voucher"
            className="fileSearchIcon"
            onClick={(e) => {
              this.trackLeaseAbstraction();
              this.openDataModal(e);
              this.handleClick(e);
            }}
            style={{ display: 'inline-block', cursor: 'pointer' }}
          >
            <LuFileSearch2 size={20} />
          </div>
        );
      } else if (!processingCompleted && file.extraction_opted_in) {
        // Show loading for opted-in files being processed
        return (
          <div className="loading-icon">
            <div className="spinner">
              <img alt="Loading" src={LoadingIcon} />
            </div>
          </div>
        );
      }
      // For opted-in files: if extraction failed, show error message but no retry icon
      // Error messages are handled elsewhere in the UI
      return null;
    }

    // For all other files (including post-upload triggered files), use the new trigger flow
    switch (extractionIconState) {
      case 'trigger':
        return (
          <div
            data-tooltip="Abstract this lease"
            className="extraction-trigger-icon"
            onClick={this.handleExtractionTrigger}
            style={{ display: 'inline-block', cursor: 'pointer', marginLeft: '10px' }}
          >
            <img
              alt="Abstract this lease"
              src={ExtractionTriggerIcon}
              style={{ height: '22px' }}
            />
          </div>
        );

      case 'loading':
        return (
          <div className="loading-icon">
            <div className="spinner">
              <img alt="Processing" src={LoadingIcon} />
            </div>
          </div>
        );

      case 'search':
        return (
          <div
            data-tooltip="Import Data to Voucher"
            className="fileSearchIcon"
            onClick={(e) => {
              this.trackLeaseAbstraction();
              this.openDataModal(e);
              this.handleClick(e);
            }}
          >
            <LuFileSearch2 size={20} />
          </div>
        );

      default:
        return null;
    }
  };

  trackNameChange = (eventValue) => {
    const {
      analyticProperties: { actionPrefix, categoryPrefix },
    } = this.props;
    userEventService.trackEvent(
      {
        eventAction: "file_name_changed",
        eventCategory: "File Action",
        eventValue,
      },
      {
        actionPrefix,
        categoryPrefix,
      }
    );
  };

  trackPrivacyChange = (eventValue) => {
    const {
      analyticProperties: { actionPrefix, categoryPrefix },
    } = this.props;
    userEventService.trackEvent(
      {
        eventAction: "file_privacy_changed",
        eventCategory: "File Action",
        eventLabel: `file_edited_from_${actionPrefix}`,
        eventValue,
      },
      {
        actionPrefix,
        categoryPrefix,
      }
    );
  };

  renderSimpleExtractionStatus = () => {
    const { extractionStatus, extractionStatusChecked, enhancedRetrying, errorMessage, successMessage, results } = this.state;
    
    // Abstraction in progress for Queued/Processing
    if (extractionStatus === 'Queued' || extractionStatus === 'Processing' || enhancedRetrying) {
      return (
        <span className="voucher-lease-import-status">Abstraction in progress&hellip;</span>
      );
    }

    // Failure state with retry
    if (extractionStatus === 'Failure') {
      return (
        <span className="voucher-lease-import-status voucher-lease-import-status--failed">
          Abstraction unavailable.{' '}
          <a
            href="#"
            className="retry-link"
            onClick={(e) => { e.preventDefault(); this.handleEnhancedRetry(); }}
          >
            Retry
          </a>
          {' '}or continue by entering lease details manually.
        </span>
      );
    }

    if (extractionStatus === 'Stopped') {
      return (
        <span className="voucher-lease-import-status voucher-lease-import-status--paused">
          Abstraction paused. If this is a Lease, click{' '}
          <span className="inline-sparkle-icon" aria-hidden="true">
            <SparkleGlyph />
          </span>
          {' '}icon to import data.
        </span>
      );
    }

    return null;
  };

    shouldShowEnhancedInProgress = () => {
    const { isEnhancedFlow } = this.props;
    const {
      enhancedRetrying,
      extractionStatus,
      extractionStatusChecked,
      processingCompleted,
    } = this.state;

    // Do not show in-progress label when sparkle is visible (Stopped state).
    if (extractionStatus === 'Stopped') {
      return false;
    }

    return isEnhancedFlow && (
      enhancedRetrying ||
      extractionStatus === 'Processing' ||
      extractionStatus === 'Queued' ||
      (extractionStatusChecked && !processingCompleted && Boolean(extractionStatus))
    );
  };

  render() {
    const { file, userData, isEnhancedFlow } = this.props;
    const {
      showEditMode,
      showModal,
      tempPrivacy,
      tempTitle,
      showDataModal,
      errorMessage,
      successMessage,
      processingCompleted,
      extractionStatusChecked,
      enhancedRetrying,
      extractionStatus,
    } = this.state;

    return (
      <React.Fragment>
        {Object.keys(file).length !== 0 && (
          <li
            className="file-list-item"
            key={file.id}
            ref={(li) => {
              this.liElement = li;
            }}
          >
            {!showEditMode && (
              <React.Fragment>
                <div className="file-content">
                  <div className="file-icon-wrapper">
                    <Icon iconName="document" />
                    <span className={`doc-type-extension doc-type-${getFileTypeClassSuffix(file)}`}>{getFileTypeDisplayLabel(file)}</span>
                  </div>
                  <div className="file-info">
                    <h3 className="filename">{file.title}</h3>
                    <div>
                      <p className="file-metadata">
                        {file.created &&
                          moment(file.created).format("YYYY-MM-DD")}{" "}
                        |<span className="privacy"> {file.privacy}</span>
                      </p>
                    </div>
                    {this.renderSimpleExtractionStatus()}
                    <p className="error-message-custom">
                      {!isEnhancedFlow && (errorMessage === "Abstraction failed" ? (
                        <>
                          Abstraction failed.&nbsp;
                          <a
                            href="#"
                            onClick={(e) => { e.preventDefault(); this.handleExtractionTrigger(); }}
                            className="retry-link"
                          >
                            Click here
                          </a>
                        &nbsp;to retry.
                        </>
                      ) : errorMessage)}
                    </p>
                    <p className="success-message-custom">{successMessage}</p>
                  </div>
                </div>
                <div className="file-actions">
                  <Avatar
                    enableHover
                    firstName={file.created_by.first_name}
                    image={file.created_by.avatar}
                    lastName={file.created_by.last_name}
                    sizing="md"
                  />
                  {extractionStatus === 'Stopped' && (
                    <div className="files-tab-sparkle-tooltip">
                      <button
                        type="button"
                        className="files-tab-sparkle-icon files-tab-sparkle-icon-button"
                        onClick={this.handleEnhancedRetry}
                        aria-label="Import Data"
                        disabled={enhancedRetrying}
                      >
                        <SparkleGlyph />
                      </button>
                      <span className="files-tab-sparkle-tooltip__label" role="tooltip">Import Data</span>
                    </div>
                  )}
                  <IconButton
                    iconName="download"
                    onClick={() => this.handleFileDownload(file.id)}
                  />
                  {file.created_by.id === userData.id && (
                    <IconButton
                      iconName="edit"
                      onClick={() => this.showEditMode()}
                    />
                  )}
                  {/* All extraction icon logic consolidated */}
                  {this.renderExtractionIcon()}
                </div>
              </React.Fragment>
            )}
            {showEditMode && (
              <div className="edit-file">
                <div className="edit-file__name">
                  <div className="file-icon-wrapper" aria-hidden="true">
                    <Icon iconName="document" />
                    <span className={`doc-type-extension doc-type-${getFileTypeClassSuffix(file)}`}>{getFileTypeDisplayLabel(file)}</span>
                  </div>
                  <input
                    type="text"
                    value={tempTitle}
                    onChange={(e) => this.handleFileNameChange(e.target.value)}
                  />
                </div>
                <div className="edit-file__actions">
                  <Checkbox
                    checked={tempPrivacy}
                    onChange={({ target: { checked } }) =>
                      this.handlePrivacyChange(checked)
                    }
                  >
                    Private
                  </Checkbox>
                  <div className="button-controls">
                    <IconButton
                      iconName="bin"
                      onClick={this.showConfirmationModal}
                      variant="basic"
                    />
                    <Button variant="secondary" onClick={this.closeEditMode}>
                      Cancel
                    </Button>
                    <Button onClick={this.handleSave}>Save</Button>
                  </div>
                </div>
              </div>
            )}
          </li>
        )}
        {showModal && (
          <ConfirmationModal
            className="deal-file-delete-confirmation"
            onCloseModal={this.closeConfirmationModal}
            showModal={showModal}
          >
            <h1>Are you sure?</h1>
            <p>This file will be permanently deleted from this deal.</p>
            <footer>
              <Button variant="secondary" onClick={this.closeConfirmationModal}>
                Cancel
              </Button>
              <Button onClick={() => this.handleDeleteFile(file.id)}>
                Delete
              </Button>
            </footer>
          </ConfirmationModal>
        )}
        {showDataModal && (
          <div className="modal-backdrop" onClick={this.hideDataModal}>
            <FileModal
              hideDataModal={this.hideDataModal}
              closeDataModal={this.closeDataModal}
              results={this.state.results}
              file={this.props.file}
              analyticProperties={this.props.analyticProperties}
              deal={this.props.file.deal}
            />
          </div>
        )}
      </React.Fragment>
    );
  }
}

FileListItem.propTypes = {
  analyticProperties: PropTypes.shape({
    actionPrefix: PropTypes.string,
    categoryPrefix: PropTypes.string,
  }),
  deleteFile: PropTypes.func.isRequired,
  dealType: PropTypes.string,
  isEnhancedFlow: PropTypes.bool,
  fetchFilesDownloadLink: PropTypes.func.isRequired,
  file: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    deal: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    document_name: PropTypes.string,
    title: PropTypes.string,
    file_type: PropTypes.string,
    file_size: PropTypes.number,
    created_by: PropTypes.shape({
      id: PropTypes.number,
      first_name: PropTypes.string,
      last_name: PropTypes.string,
      avatar: PropTypes.string,
      email: PropTypes.shape({
        work: PropTypes.string,
      }),
    }),
    created: PropTypes.string,
    modified: PropTypes.string,
    can_edit: PropTypes.bool,
    privacy: PropTypes.string,
    is_lease_document: PropTypes.bool,
    extraction_opted_in: PropTypes.bool,
  }).isRequired,
  updateFile: PropTypes.func.isRequired,
  showToast: PropTypes.func.isRequired,
  userData: PropTypes.shape({
    id: PropTypes.number,
  }).isRequired,
};

FileListItem.defaultProps = {
  analyticProperties: {
    actionPrefix: '',
    categoryPrefix: '',
  },
  dealType: null,
  isEnhancedFlow: false,
};

const mapDispatchToProps = dispatch => ({
  leaseFileUpdate: file => dispatch(abstractionLeaseFile(file)),
  showToast: message => dispatch(toast(message)),
});

export default connect(null, mapDispatchToProps)(FileListItem);
